import React from 'react';
import clsx from 'clsx';

export type TypographyVariant =
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'h7'
  | 'body1' | 'body2' | 'body3' | 'body4'
  | 'label1' | 'label2' | 'label2SemiBold' | 'label3' | 'label3SemiBold' | 'label4' | 'label5'
  | 'button1' | 'button1light' | 'button2';

const variantMap: Record<TypographyVariant, { tag: React.ElementType; className: string }> = {
  h1: { tag: 'h1', className: 'text-[#11181C] text-[50px] font-semibold leading-auto tracking-[-2%]' },
  h2: { tag: 'h2', className: 'text-[#11181C] text-[44px] font-semibold leading-[56px] tracking-[-2%]' },
  h3: { tag: 'h3', className: 'text-[#11181C] text-[32px] font-semibold leading-[38px] tracking-[-2%]' },
  h4: { tag: 'h4', className: 'text-[#11181C] text-[28px] font-semibold leading-auto tracking-normal' },
  h5: { tag: 'h5', className: 'text-[#11181C] text-[24px] font-semibold leading-[24px] tracking-normal' },
  h6: { tag: 'h6', className: 'text-[#11181C] text-[20px] font-medium leading-auto tracking-normal' },
  h7: { tag: 'h6', className: 'text-[#11181C] text-[16px] font-semibold leading-auto tracking-normal' },
  body1: { tag: 'p', className: 'text-[#11181C] text-[16px] font-regular leading-[22px] tracking-normal' },
  body2: { tag: 'p', className: 'text-[#11181C] text-[14px] font-regular leading-[18px] tracking-normal' },
  body3: { tag: 'p', className: 'text-[#11181C] text-[12px] font-regular leading-[16px] tracking-normal' },
  body4: { tag: 'p', className: 'text-[#11181C] text-[10px] font-regular leading-[16px] tracking-normal' },
  label1: { tag: 'span', className: 'text-[#11181C] text-[16px] font-medium leading-[20px] tracking-normal' },
  label2: { tag: 'span', className: 'text-[#11181C] text-[14px] font-medium leading-[18px] uppercase tracking-[0.04em]' },
  label2SemiBold: { tag: 'span', className: 'text-[#11181C] text-[14px] font-semibold leading-[18px] tracking-normal' },
  label3: { tag: 'span', className: 'text-[#11181C] text-[12px] font-medium leading-[16px]  tracking-[0.04em]' },
  label3SemiBold: { tag: 'span', className: 'text-[#11181C] text-[12px] font-semibold leading-[16px] tracking-normal' },
  label4: { tag: 'span', className: 'text-[10px] font-medium leading-[12px] uppercase tracking-[0.04em]' },
  label5: { tag: 'span', className: 'text-[#11181C] text-[8px] font-medium leading-[10px] uppercase tracking-[0.04em]' },
  button1: { tag: 'span', className: 'text-[#11181C] text-[16px] font-semibold leading-[20px] uppercase tracking-[0.04em]' },
  button1light: { tag: 'span', className: 'text-[#11181C] text-[16px] font-normal leading-[20px] uppercase tracking-[0.04em]' },
  button2: { tag: 'span', className: 'text-[#11181C] text-[14px] font-semibold leading-[18px] uppercase tracking-[0.04em]' },
};

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant: TypographyVariant;
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
}

export const Typography: React.FC<TypographyProps> = ({ variant, as, children, className, ...rest }) => {
  const { tag, className: variantClass } = variantMap[variant];
  const Tag = as || tag;
  return (
    <Tag className={clsx(variantClass, className)} {...rest}>
      {children}
    </Tag>
  );
};

export default Typography; 
